import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { AlertMessage } from './ui/AlertMessage';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { DetailField, DetailSection } from './ui/DetailSection';
import { AuthenticatedImage } from './ui/AuthenticatedImage';
import { API_BASE_URL } from '../api/mediaUrl';
import { OrderDetail } from './orders/OrderDetail';
import { useModuleAccess } from '../permissions/useModuleAccess';
import { useCommercialAccess } from '../permissions/useCommercialAccess';
import {
  parseProductsOverlay,
  productsNavigateTarget,
} from './products/productsRouteUtils';
import {
  applyDesignListFilters,
  countActiveDesignFilters,
  DEFAULT_DESIGN_LIST_FILTERS,
} from './designs/designListFilterUtils';
import { DesignListFiltersPanel } from './designs/DesignListFiltersPanel';

function sortDesignsRecentFirst(list) {
  return [...list].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (tb !== ta) return tb - ta;
    return String(b.id).localeCompare(String(a.id));
  });
}

/**
 * Concept card: ~70% image area, ~30% label (matches reference layout).
 */
function ConceptCard({ label, coverSrc, selected, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        'flex flex-col w-full rounded-xl overflow-hidden bg-white border text-left transition-all',
        'shadow-md hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        'aspect-[5/7] max-h-[280px]',
        selected ? 'ring-2 ring-primary-500 border-primary-300' : 'border-gray-200',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
      ].join(' ')}
    >
      <div className="relative flex-[7] min-h-0 w-full bg-gray-100">
        {coverSrc ? (
          <AuthenticatedImage
            src={coverSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 text-gray-400 text-[10px] uppercase tracking-wide">
            No preview
          </div>
        )}
      </div>
      <div className="flex-[3] flex min-h-0 items-center justify-center px-2 py-2 border-t border-gray-100 bg-white">
        <span className="text-[10px] sm:text-[11px] font-semibold text-gray-800 uppercase tracking-wide text-center leading-tight line-clamp-3">
          {label}
        </span>
      </div>
    </button>
  );
}

/** Large image-only tile in gallery */
function GalleryImage({ src, onClick }) {
  if (!src) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="aspect-[3/4] rounded-lg overflow-hidden border border-gray-200 bg-gray-100 shadow-sm hover:ring-2 hover:ring-primary-400 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      <AuthenticatedImage src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
    </button>
  );
}

/**
 * Products:
 * — Concepts: "All Products", then all categories.
 * — Click All Products → all designs (images only), newest → oldest.
 * — Click category → that category’s designs only.
 * — Overlay stack (category → design preview → order) is synced to URL query params
 *   so browser Back closes each layer without leaving /products.
 */
function Products() {
  const navigate = useNavigate();
  const location = useLocation();
  const galleryRef = useRef(null);

  const { canCreate: canCreateOrder } = useModuleAccess('ORDERS');
  const canSeeCommercial = useCommercialAccess();
  const canOrderDesign = canCreateOrder && canSeeCommercial;

  const overlay = useMemo(
    () => parseProductsOverlay(location.search),
    [location.search]
  );

  const selectedKey = overlay.category;

  const [designs, setDesigns] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [productFilters, setProductFilters] = useState(() => ({ ...DEFAULT_DESIGN_LIST_FILTERS }));
  const [filtersOpen, setFiltersOpen] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageError('');
    try {
      const [designRes, catRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/designs`, { headers }),
        fetch(`${API_BASE_URL}/api/insights?type=CATEGORY`, { headers }),
      ]);
      if (!designRes.ok) throw new Error('Unable to load designs');
      if (!catRes.ok) throw new Error('Unable to load categories');
      const designData = await designRes.json();
      const catData = await catRes.json();
      setDesigns(Array.isArray(designData) ? designData : []);
      setCategoryOptions(Array.isArray(catData) ? catData : []);
    } catch (e) {
      setPageError(e?.message || 'Failed to load products');
      setDesigns([]);
      setCategoryOptions([]);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const withImages = useMemo(
    () => designs.filter((d) => String(d.designImage || '').trim()),
    [designs]
  );

  const filteredWithImages = useMemo(
    () => applyDesignListFilters(withImages, productFilters),
    [withImages, productFilters]
  );

  const filterBadge = countActiveDesignFilters(productFilters);

  const conceptCategoryRows = useMemo(() => {
    const byId = new Map();
    categoryOptions.forEach((c) => {
      if (c?.id == null) return;
      const id = String(c.id);
      byId.set(id, { id, name: String(c.name || '').trim() || `Category ${id}` });
    });
    filteredWithImages.forEach((d) => {
      if (d.categoryId == null) return;
      const id = String(d.categoryId);
      if (!byId.has(id)) {
        byId.set(id, { id, name: String(d.categoryName || '').trim() || `Category ${id}` });
      }
    });
    return Array.from(byId.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [categoryOptions, filteredWithImages]);

  const coverForCategory = useCallback(
    (categoryId) => {
      const list = filteredWithImages.filter((d) => String(d.categoryId) === String(categoryId));
      const first = sortDesignsRecentFirst(list)[0];
      return first?.designImage || '';
    },
    [filteredWithImages]
  );

  const allProductsCover = useMemo(() => {
    const first = sortDesignsRecentFirst(filteredWithImages)[0];
    return first?.designImage || '';
  }, [filteredWithImages]);

  const galleryDesigns = useMemo(() => {
    if (selectedKey === null) return [];
    if (selectedKey === 'all') return sortDesignsRecentFirst(filteredWithImages);
    return sortDesignsRecentFirst(
      filteredWithImages.filter((d) => String(d.categoryId) === String(selectedKey))
    );
  }, [selectedKey, filteredWithImages]);

  const galleryTitle = useMemo(() => {
    if (selectedKey === null) return '';
    if (selectedKey === 'all') return 'All products';
    return conceptCategoryRows.find((c) => String(c.id) === String(selectedKey))?.name || 'Category';
  }, [selectedKey, conceptCategoryRows]);

  const previewDesign = useMemo(() => {
    if (!overlay.design) return null;
    return filteredWithImages.find((d) => String(d.id) === String(overlay.design)) || null;
  }, [overlay.design, filteredWithImages]);

  const pushOverlay = useCallback(
    (next) => {
      navigate(productsNavigateTarget(next));
    },
    [navigate]
  );

  const openCategory = useCallback(
    (key) => {
      pushOverlay({ category: key });
    },
    [pushOverlay]
  );

  const openDesign = useCallback(
    (designId) => {
      pushOverlay({ category: overlay.category, design: designId });
    },
    [overlay.category, pushOverlay]
  );

  const closeDesign = useCallback(() => {
    navigate(productsNavigateTarget({ category: overlay.category }), { replace: true });
  }, [overlay.category, navigate]);

  const closeCategory = useCallback(() => {
    navigate(productsNavigateTarget({}), { replace: true });
  }, [navigate]);

  const openOrder = useCallback(() => {
    if (!overlay.design) return;
    pushOverlay({ category: overlay.category, design: overlay.design, order: 'new' });
  }, [overlay.category, overlay.design, pushOverlay]);

  const closeOrder = useCallback(() => {
    navigate(
      productsNavigateTarget({ category: overlay.category, design: overlay.design }),
      { replace: true }
    );
  }, [overlay.category, overlay.design, navigate]);

  const handleDraftCreated = useCallback(
    (orderId) => {
      navigate(
        productsNavigateTarget({
          category: overlay.category,
          design: overlay.design,
          order: String(orderId),
        }),
        { replace: true }
      );
    },
    [navigate, overlay.category, overlay.design]
  );

  /** Deep-link with design but no category — infer category from the design row. */
  useEffect(() => {
    if (!overlay.design || overlay.category || loading) return;
    const design = withImages.find((d) => String(d.id) === String(overlay.design));
    if (!design) return;
    const category = design.categoryId != null ? String(design.categoryId) : 'all';
    navigate(
      productsNavigateTarget({
        category,
        design: overlay.design,
        order: overlay.order,
      }),
      { replace: true }
    );
  }, [overlay.design, overlay.category, overlay.order, loading, withImages, navigate]);

  /** Scroll gallery into view when a category is selected (including from URL / back). */
  useEffect(() => {
    if (!overlay.category || overlay.order) return undefined;
    const t = setTimeout(() => {
      galleryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [overlay.category, overlay.order]);

  /** Strip invalid category from URL once data is loaded. */
  useEffect(() => {
    if (!overlay.category || overlay.category === 'all' || loading) return;
    const valid = conceptCategoryRows.some((c) => String(c.id) === String(overlay.category));
    if (!valid) {
      navigate(productsNavigateTarget({ design: overlay.design, order: overlay.order }), { replace: true });
    }
  }, [overlay.category, overlay.design, overlay.order, conceptCategoryRows, loading, navigate]);

  /** Design id in URL but not found — drop preview layer. */
  useEffect(() => {
    if (!overlay.design || loading) return;
    if (!previewDesign) {
      navigate(
        productsNavigateTarget({ category: overlay.category, order: overlay.order }),
        { replace: true }
      );
    }
  }, [overlay.design, overlay.category, overlay.order, previewDesign, loading, navigate]);

  /** Unauthorized order overlay — fall back to design preview. */
  useEffect(() => {
    if (!overlay.order || canOrderDesign) return;
    navigate(
      productsNavigateTarget({ category: overlay.category, design: overlay.design }),
      { replace: true }
    );
  }, [overlay.order, overlay.category, overlay.design, canOrderDesign, navigate]);

  const showDesignPreview = Boolean(overlay.design && previewDesign && !overlay.order);
  const showOrderOverlay = Boolean(overlay.order && canOrderDesign);

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title="Products"
            actions={(
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setFiltersOpen((open) => !open)}
                className="w-full shrink-0 sm:w-auto"
              >
                <svg
                  className="mr-1.5 h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filters
                {filterBadge > 0 ? (
                  <span className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary-600 px-1.5 text-[10px] font-bold text-white">
                    {filterBadge}
                  </span>
                ) : null}
              </Button>
            )}
          />
        </CardHeader>
        <CardBody className="pt-6 space-y-8">
          <AlertMessage message={pageError} type="error" onClose={() => setPageError('')} className="mb-2" />
          {filtersOpen ? (
            <DesignListFiltersPanel
              title="Filter products"
              value={productFilters}
              onApply={setProductFilters}
              items={withImages}
            />
          ) : null}
          {loading ? (
            <div className="py-16 text-center text-gray-500">Loading…</div>
          ) : (
            <>
              <section>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Concepts</h2>
                  {filterBadge > 0 ? (
                    <p className="text-xs text-slate-500">
                      {filteredWithImages.length} product{filteredWithImages.length === 1 ? '' : 's'} match filters
                    </p>
                  ) : null}
                </div>
                {filteredWithImages.length === 0 ? (
                  <p className="text-sm text-gray-500">No products match your filters.</p>
                ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  <ConceptCard
                    label="All Products"
                    coverSrc={allProductsCover}
                    selected={selectedKey === 'all'}
                    onClick={() => {
                      if (selectedKey !== 'all') openCategory('all');
                    }}
                  />
                  {conceptCategoryRows.map((cat) => (
                    <ConceptCard
                      key={cat.id}
                      label={cat.name}
                      coverSrc={coverForCategory(cat.id)}
                      selected={selectedKey === cat.id}
                      onClick={() => {
                        if (String(selectedKey) !== String(cat.id)) openCategory(cat.id);
                      }}
                    />
                  ))}
                </div>
                )}
              </section>

              {selectedKey !== null && (
                <section ref={galleryRef} className="border-t border-gray-100 pt-8">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <h2 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{galleryTitle}</h2>
                    <Button type="button" variant="outline" size="sm" onClick={closeCategory}>
                      Back to concepts
                    </Button>
                  </div>
                  {galleryDesigns.length === 0 ? (
                    <p className="text-sm text-gray-400">
                      {filterBadge > 0
                        ? 'No products in this category match your filters.'
                        : 'No design images in this selection.'}
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                      {galleryDesigns.map((d) => (
                        <GalleryImage
                          key={d.id}
                          src={d.designImage}
                          onClick={() => openDesign(d.id)}
                        />
                      ))}
                    </div>
                  )}
                </section>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showDesignPreview}
        onClose={closeDesign}
        title="Design preview"
        size="2xl"
        bodyClassName="bg-slate-50/50"
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )}
        footer={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canOrderDesign && (
              <Button type="button" onClick={openOrder}>
                Order this design
              </Button>
            )}
            <Button type="button" variant="secondary" onClick={closeDesign}>
              Close
            </Button>
          </div>
        )}
      >
        {previewDesign && (
          <div className="py-5 sm:py-6 grid grid-cols-1 lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 rounded-2xl border border-slate-200/80 bg-white shadow-sm min-h-[280px] flex items-center justify-center overflow-hidden">
              {previewDesign.designImage ? (
                <AuthenticatedImage
                  src={previewDesign.designImage}
                  alt={previewDesign.sampleNo || 'Design preview'}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-sm text-slate-500 py-16">No image available</div>
              )}
            </div>
            <div className="lg:col-span-2">
              <DetailSection title="Classification" columns={1}>
                <DetailField label="Sample number" value={previewDesign.sampleNo} mono />
                <DetailField label="Concept" value={previewDesign.conceptName} />
                <DetailField label="Category" value={previewDesign.categoryName} />
                <DetailField label="Sub category" value={previewDesign.subCategoryName} />
              </DetailSection>
            </div>
          </div>
        )}
      </Modal>

      {showOrderOverlay && (
        <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-50">
          <div className="mx-auto flex min-h-0 w-full max-w-[1500px] flex-col px-3 py-3 sm:px-4 sm:py-4">
            <OrderDetail
              key={overlay.order === 'new' ? 'new' : overlay.order}
              orderId={overlay.order === 'new' ? null : overlay.order}
              initialSelectDesignId={overlay.design}
              onBack={closeOrder}
              onDraftCreated={handleDraftCreated}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
